import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import BonacciStudioIcon from './BonacciStudioIcon';
import PublicShell from './PublicShell';

/**
 * AuthCard — split-panel layout for auth-related pages. Left side is the
 * brand hero (icon + Studio wordmark + tagline). Right side is a
 * surface card containing the form. Mobile collapses to a stacked layout.
 *
 * Props:
 *   children     — form content (rendered inside the right-hand card)
 *   title        — heading shown above the form
 *   subtitle     — subheading below the title
 *   backTo       — path for the "back" link in nav (default '/')
 *   backLabel    — label for the back link (default 'Back to home')
 *   tagline      — line under the wordmark in left hero (overridable)
 *   description  — short description below tagline
 *   footerNote   — small text under the card (e.g., links to Terms/Privacy)
 */
const AuthCard = ({
  children,
  title,
  subtitle,
  backTo = '/',
  backLabel = 'Back to home',
  tagline = 'Agentic AI data engineering platform',
  description = 'Pipelines, quality, streaming & AI agents, built from scratch by ThinkingDBx.',
  footerNote = null,
}) => {
  const navRight = (
    <Link
      to={backTo}
      className="text-sm text-white/50 hover:text-white transition-colors inline-flex items-center gap-1.5"
    >
      <ArrowLeft size={14} /> {backLabel}
    </Link>
  );

  return (
    <PublicShell navRight={navRight} showFooter={false} flowerFocusX={0.3}>
      <div className="flex flex-1 min-h-[calc(100vh-65px)]">
        {/* Left — brand hero */}
        <div className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center p-12 relative">
          {/* Local glow centered on icon */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full bg-violet-500/10 blur-[80px] pointer-events-none" />

          <div className="relative z-10 text-center max-w-md">
            <div className="relative inline-block mb-5">
              <div className="absolute inset-0 scale-150 rounded-full bg-violet-500/20 blur-2xl" />
              <BonacciStudioIcon size={84} className="relative z-10" />
            </div>
            <h1
              className="text-5xl lg:text-6xl tracking-tight mb-3 bg-clip-text text-transparent bg-gradient-to-b from-white to-violet-200"
              style={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600 }}
            >
              Studio
            </h1>
            <p
              className="text-white/65 text-base font-medium leading-relaxed mb-3"
              style={{ fontFamily: '"Space Grotesk", sans-serif' }}
            >
              {tagline}
            </p>
            <p className="text-sm text-white/40 max-w-sm mx-auto leading-relaxed">{description}</p>
            <p className="mt-5 text-xs text-white/30" style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}>
              φ = 1.6180339887…
            </p>
          </div>
        </div>

        {/* Vertical divider on desktop */}
        <div className="hidden lg:block w-px bg-white/5" />

        {/* Right — form */}
        <div className="w-full lg:w-1/2 flex flex-col items-center justify-center px-6 py-12">
          {/* Mobile-only brand block */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center gap-2.5 mb-2">
              <BonacciStudioIcon size={32} />
              <h1
                className="text-2xl tracking-tight"
                style={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600 }}
              >
                Studio
              </h1>
            </div>
            <p className="text-sm text-white/60" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
              {tagline}
            </p>
          </div>

          <div className="w-full max-w-md">
            <div
              className="rounded-[18px] border border-white/[0.08] p-7 shadow-[0_40px_90px_-45px_rgba(0,0,0,0.95)] backdrop-blur-xl"
              style={{ background: 'linear-gradient(180deg, rgba(16,14,24,0.9), rgba(10,9,15,0.92))' }}
            >
              {(title || subtitle) && (
                <div className="mb-6">
                  {title && <h2 className="text-[20px] font-semibold tracking-tight mb-1.5">{title}</h2>}
                  {subtitle && <p className="text-sm text-white/55">{subtitle}</p>}
                </div>
              )}
              {children}
            </div>

            {footerNote && (
              <div className="mt-5 text-center text-xs text-white/35" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                {footerNote}
              </div>
            )}
          </div>
        </div>
      </div>
    </PublicShell>
  );
};

export default AuthCard;
